<template>
  <v-app>
    <!-- Loading State -->
    <div v-if="loading" class="loading-screen">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>

    <!-- Error State -->
    <div v-if="error && !loading" class="error-container">
      <v-container>
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="6" class="text-center">
            <v-icon size="80" color="grey">mdi-alert-circle-outline</v-icon>
            <h2 class="mt-4">{{ error }}</h2>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Main Content -->
    <v-main v-else-if="!loading" class="modern-main">
      <v-container class="py-8">
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <!-- Header -->
            <div class="modern-header text-center mb-12">
              <div class="menu-title-wrapper">
                <div class="decorative-star left">✦</div>
                <h1 class="menu-title">{{ displayRestaurantName }}</h1>
                <div class="decorative-star right">✦</div>
              </div>
              <div class="decorative-line"></div>
              <p v-if="locationAddress" class="location-address">{{ locationAddress }}</p>
            </div>

            <!-- Categories and Items -->
            <div v-if="filteredCategories.length > 0">
              <div
                v-for="category in filteredCategories"
                :key="category.id"
                class="category-section mb-10"
              >
                <!-- Category Header -->
                <div class="category-header-wrapper text-center mb-6">
                  <div class="category-stars">✦ ✦</div>
                  <div class="category-header">
                    {{ category.name.toUpperCase() }}
                  </div>
                  <div class="category-stars">✦ ✦</div>
                </div>

                <!-- Category Items -->
                <div class="items-list">
                  <div
                    v-for="item in getCategoryItems(category.id)"
                    :key="item.id"
                    class="menu-item"
                  >
                    <div class="item-content">
                      <div class="item-info">
                        <span class="item-name">{{ item.name }}</span>
                        <span v-if="item.description" class="item-description">{{ item.description }}</span>
                      </div>
                      <div class="item-dots"></div>
                      <div class="item-price">{{ formatPrice(getItemPrice(item)) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <v-card v-else flat class="text-center pa-12" style="background: transparent;">
              <v-icon size="80" color="grey lighten-1">mdi-food-off</v-icon>
              <p class="text-h6 mt-4 grey--text">Trenutno nema dostupnih artikala</p>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
export default {
  name: 'PublicMenuModern',
  props: {
    menuData: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      error: null
    }
  },
  computed: {
    displayRestaurantName () {
      return this.menuData?.location?.name || this.menuData?.company?.name || 'MENI'
    },
    locationAddress () {
      const location = this.menuData?.location
      if (!location) return ''
      return `${location.address || ''}, ${location.city || ''}`.trim().replace(/^,\s*/, '')
    },
    menuSettings () {
      return this.menuData?.location?.menuSettings || {}
    },
    items () {
      return this.menuData?.items || []
    },
    categories () {
      return this.menuData?.categories || []
    },
    filteredCategories () {
      // Filter out categories with no items
      return this.categories.filter(cat => {
        return this.getCategoryItems(cat.id).length > 0
      })
    }
  },
  methods: {
    getCategoryItems (categoryId) {
      return this.items.filter(item =>
        item.category_ids && item.category_ids.includes(categoryId)
      ).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },

    getItemPrice (item) {
      if (!item || !item.prices) return 0

      const currency = this.menuSettings.default_currency || 'EUR'
      const priceData = item.prices[currency]

      if (!priceData || !priceData.active) return 0

      return priceData.price || 0
    },

    formatPrice (priceInCents) {
      if (!priceInCents) return '0,00 €'

      const currency = this.menuSettings.default_currency || 'EUR'
      const symbol = currency === 'EUR' ? '€' : 'kn'

      return (priceInCents / 100)
        .toFixed(2)
        .replace('.', ',') + ' ' + symbol
    }
  }
}
</script>

<style scoped>
.modern-main {
  background: linear-gradient(to bottom, #f9f6f0 0%, #ffffff 100%);
  min-height: 100vh;
  font-family: 'Georgia', serif;
}

.loading-screen {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f9f6f0;
}

.error-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: #f9f6f0;
}

/* Header Styles */
.modern-header {
  padding: 32px 16px;
  margin-bottom: 24px;
}

.menu-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 12px;
}

.menu-title {
  font-size: 2.5rem;
  font-weight: 400;
  letter-spacing: 0.15em;
  color: #5d4037;
  margin: 0;
  text-transform: uppercase;
}

.decorative-star {
  color: #d4a574;
  font-size: 1.5rem;
}

.decorative-line {
  height: 2px;
  background: repeating-linear-gradient(
    to right,
    #d4a574 0px,
    #d4a574 10px,
    transparent 10px,
    transparent 15px
  );
  max-width: 400px;
  margin: 12px auto;
}

.location-address {
  color: #8d6e63;
  font-size: 1rem;
  margin-top: 8px;
  font-style: italic;
}

/* Category Styles */
.category-section {
  margin-bottom: 48px;
}

.category-header-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 32px;
}

.category-stars {
  color: #d4a574;
  font-size: 1.2rem;
  letter-spacing: 0.5em;
}

.category-header {
  background: #8d6e63;
  color: #ffffff;
  padding: 12px 48px;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(141, 110, 99, 0.3);
}

/* Items List Styles */
.items-list {
  background: rgba(255, 255, 255, 0.6);
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.menu-item {
  margin-bottom: 16px;
  padding-bottom: 16px;
}

.menu-item:not(:last-child) {
  border-bottom: 1px solid rgba(141, 110, 99, 0.1);
}

.item-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
}

.item-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.item-name {
  font-size: 1.1rem;
  color: #3e2723;
  font-weight: 500;
  line-height: 1.4;
}

.item-description {
  font-size: 0.9rem;
  color: #8d6e63;
  font-style: italic;
  margin-top: 4px;
  line-height: 1.3;
}

.item-dots {
  flex: 1;
  border-bottom: 2px dotted rgba(141, 110, 99, 0.3);
  margin: 0 8px;
  align-self: flex-end;
  margin-bottom: 8px;
}

.item-price {
  font-size: 1.1rem;
  color: #5d4037;
  font-weight: 600;
  white-space: nowrap;
  min-width: 70px;
  text-align: right;
}

/* Mobile Responsive */
@media (max-width: 600px) {
  .menu-title {
    font-size: 1.8rem;
    letter-spacing: 0.1em;
  }

  .decorative-star {
    font-size: 1.2rem;
  }

  .category-header {
    padding: 10px 32px;
    font-size: 0.9rem;
  }

  .category-stars {
    font-size: 1rem;
  }

  .items-list {
    padding: 16px;
  }

  .item-name {
    font-size: 1rem;
  }

  .item-price {
    font-size: 1rem;
    min-width: 60px;
  }

  .item-dots {
    margin: 0 6px;
  }
}
</style>
